




































import mixins from 'vue-typed-mixins';
import { DataTableHeader, DataTableCompareFunction } from 'vuetify';
import SurveyUtils, {
  Question,
  Survey,
  Section,
  Page,
} from '@/utils/SurveyUtils';
import SurveyQuestionDuplicate from '@/components/survey/management/SurveyQuestionDuplicate.vue';
import SurveyQuestionModal from '@/components/survey/management/questions/SurveyQuestionModal.vue';
import cloneDeep from 'lodash.clonedeep';
import SurveyNodeUtils from '@/utils/SurveyNodeUtils';
import { ReportStore, ReportVM } from '@/utils/ReportUtils';
export default mixins().extend({
  components: {
    SurveyQuestionDuplicate,
    SurveyQuestionModal,
  },

  props: {
    value: {
      type: Object as () => Survey,
    },
  },

  computed: {
    headers(): DataTableHeader<Question>[] {
      return [
        {
          text: this.$i18n.tc('model.question.fullquestionnumber'),
          value: 'fullquestionnumber',
        },
        { text: this.$i18n.tc('model.common.name'), value: 'shorttext' },
        { text: this.$i18n.tc('model.common.key'), value: 'questionkey' },
        { text: this.$i18n.tc('model.common.type'), value: 'questiontype.key' },
      ];
    },
  },

  data(): {
    questions?: Question[];
  } {
    return {
      questions: undefined,
    };
  },

  async mounted() {
    try {
      this.questions = await SurveyUtils.api.question.list({
        surveyid: this.value.id,
      });
    } catch (error) {
      this.$alert.show('mount', {
        type: 'error',
        messages: this.$handleAPIError(error),
      });
    }
  },

  methods: {
    async create() {
      (this.$refs.questionModal as any).open({ survey: this.value });
    },

    async edit(question: Question) {
      (this.$refs.questionModal as any).open(question);
    },

    async remove(question: Question) {
      await this.$confirm(
        this.$i18n
          .t('validation.confirmation.delete', [question.questionkey])
          .toString(),
        async () => {
          if (question.id && this.questions) {
            this.$alert.hide('remove');
            await SurveyUtils.api.question.remove(question.id);

            this.$toast.push({
              type: 'success',
              text: this.$languageobj('validation.success.delete', {
                values: [question.questionkey],
              }),
            });

            const index = this.questions.findIndex(
              (val) => val.id === question.id
            );
            if (index >= 0) {
              this.questions.splice(index, 1);
            }

            // recursivly loop through layout and remove the question from any page it was assigned to
            if (question.id) {
              const recursiveQuestionFormatter = (
                items: Partial<Section | Page>[]
              ) => {
                for (let i = 0; i < items.length; i++) {
                  if (items[i].type === 'section') {
                    const section = items[i] as Section;
                    recursiveQuestionFormatter(section.children);
                  } else if (items[i].type === 'page') {
                    const page = items[i] as Page;
                    const index = page.questions.findIndex(
                      (val) => val.id === question.id
                    );
                    if (index >= 0) {
                      page.questions.splice(index, 1);
                      this.$emit('pristine', this.value); // update pristine to not block page navigation
                    }
                  }
                }
              };
              recursiveQuestionFormatter(this.value.layout);
            }
          }
        }
      );
    },

    duplicate(question: Question) {
      (this.$refs.duplicateModal as any).open(question);
    },

    duplicateSuccess(question: Question) {
      // if the duplicated question belongs to this survey, add it to the list
      if (question.survey.id === this.value.id) {
        this.questions?.push(question);
        this.questions = Array.from(this.questions as Question[]);
      }
    },

    async submit(question: Question) {
      this.$alert.hide('submit');
      if (question.id) {
        await SurveyUtils.api.question.update(question);

        if (this.questions) {
          const index = this.questions.findIndex(
            (val) => val.id === question?.id
          );
          if (index >= 0) {
            this.questions[index] = question;
          }
        }

        this.$toast.push({
          type: 'success',
          text: this.$languageobj('validation.success.default', {
            values: [question.questionkey],
          }),
        });
      } else {
        question.id = await SurveyUtils.api.question.add(question);
        this.questions?.push(question);
        this.$toast.push({
          type: 'success',
          text: this.$languageobj('validation.success.add', {
            values: [question.questionkey],
          }),
        });
      }
      this.questions = Array.from(this.questions || []);
    },

    async view(question: Question) {
      const layout = cloneDeep(this.value?.layout);

      // merge questions into layout for node generation
      ReportVM.mergeQuestions(layout, this.questions as Question[]);

      // generate nodes for the survey to find the path
      const nodes = SurveyNodeUtils.generateNodes(
        {
          ...ReportStore,
          mode: ['preview'],
          survey: this.value, // the survey must be set here instead of waiting for the preview to mount or the path will not be correct
        },
        layout
      );

      // find the node for the question
      const node = SurveyNodeUtils.getNode(nodes, (node) => {
        return node.item?.id === question.id;
      });

      if (node) {
        // generate the path for the URL and launch the preview
        const path = SurveyNodeUtils.generatePath(node, nodes).join('-');
        if (path) {
          await this.$router.push({
            name: 'survey-edit-preview',
            params: {
              id: `${this.value?.id || ''}`,
              path: path,
            },
          });
        }
      }
    },
  },
});
